import React from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import styled from '@emotion/styled';

import useUserContext from '../../hooks/use-user-context';
import { createOptionMap } from '../../utilities/misc';
import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';
import { fontWeights } from '../../theme/type';

import Dropdown from '../dropdown/Dropdown';
import DropdownLabel from '../dropdown/DropdownLabel';
import DropdownMenu from '../dropdown/DropdownMenu';
import DropdownPanel from '../dropdown/DropdownPanel';
import DropdownFocusWrapper from '../dropdown/DropdownFocusWrapper';
import Link from '../Link';
import TextRenderer from '../TextRenderer';

const Nav = styled.nav`
  flex: 1;
`;

const TopMenu = styled.ul`
  display: flex;
  margin: 0 ${rem(-16)};
`;

const MenuItem = styled.li`

`;

const MenuLink = styled(Link)`
  position: relative;
  height: ${rem(90)};
  padding: 0 ${rem(16)};
  display: flex;
  align-items: center;
  color: ${colors.blue['800']};
  font-size: ${rem(16)};
  font-weight: ${fontWeights.semiBold};
  transition: color 150ms ease;

  ${media('>medium')} {
    font-size: ${rem(16)};
  }

  ${({ hasDropdown }) => hasDropdown && `
    &:hover::after {
      content: '';
      position: absolute;
      z-index: 1;
      inset: calc(50% + ${rem(8)}) ${rem(16)} auto;
      height: ${rem(10)};
      transform-origin: top;
      transform: perspective(${rem(8)}) rotateX(45deg);
    }
  `}

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `}
`;

const ListColumns = styled.div`
  display: flex;
  gap: ${rem(12)};

  ${media('>nav')} {
    height: 100%
  }
`;

const MenuCardsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: 1fr;
  gap: ${rem(10)};
  padding-top: ${rem(20)};
`;

const MenuCard = styled(Link)`
  display: block;
  background: ${colors.blue['50']};
  border-radius: ${rem(10)};
  color: ${colors.blue['800']};
  padding: ${rem(15)};
  cursor: pointer;
  transition: background 150ms ease;

  &:hover {
    background: ${colors.gray['200']};
    color: ${colors.blue['800']};
  }
`;

const MenuCardTitle = styled.h3`
  font-size: ${rem(16)};
  font-weight: ${fontWeights.semiBold};
`;

const MenuCardDescription = styled.p`
  padding-top: ${rem(5)};
  line-height: 1.6;
  font-size: ${rem(14)};
`;

const DropdownLabelWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: ${rem(256)};
  padding: ${rem(16)} ${rem(20)};
  border: 1px solid ${colors.gray['200']};
  border-radius: ${rem(8)};
  background: ${colors.white};

  ${({ type }) => type === 'Secondary' && `
    border-color: transparent;
    background: 0;
    border-radius: 0;
  `}
`;

const DropdownDescription = styled.p`
  padding: ${rem(4)} 0;
  font-size: ${rem(14)};
  color: ${colors.gray['600']};
`;

const DropdownLink = styled(Link)`
font-weight: ${fontWeights.medium};
  color: inherit;
  &:focus-visible,
  &:hover {
    text-decoration: underline;
  }

  &::after {
    content: '';
    position: absolute;
    z-index: 1;
    inset: 0;
  }
`;

const MenuCards = ({ items }) => (
  <MenuCardsContainer>
    {items.map((item) => (
      <MenuCard
        key={item.id}
        to={item.url}
        targetBlank={item.targetBlank}
      >
        <MenuCardTitle>
          <TextRenderer text={item.title} />
        </MenuCardTitle>
        <MenuCardDescription>
          <TextRenderer text={item.description} />
        </MenuCardDescription>
      </MenuCard>
    ))}
  </MenuCardsContainer>
);

const MenuColumns = ({ items }) => (
  <ListColumns>
    {items.map((item) => (
      <DropdownLabelWrapper key={item.id} type={item.type}>
        <DropdownLabel>
          {item.url ? (
            <DropdownLink to={item.url}>
              <TextRenderer text={item.title} />
            </DropdownLink>
          ) : (
            <TextRenderer text={item.title} />
          )}
        </DropdownLabel>
        {item.description && (
          <DropdownDescription>{item.description}</DropdownDescription>
        )}
        {item.childNavigationItems && (
          <DropdownMenu items={item.childNavigationItems} />
        )}
      </DropdownLabelWrapper>
    ))}
  </ListColumns>
);

const NavbarMainMenu = ({ type, className, setIsDropdownOpen }) => {
  const data = useStaticQuery(graphql`
    fragment NavbarMainMenuNavigationItemFragment on ContentfulNavigationItem {
      id
      title
      url
      description
      navPill
      targetBlank
      backgroundColor
      type
    }

    query NavbarMainMenu {
      allContentfulOptionSet(filter: {name: {eq: "navbar"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
      allContentfulMegaMenu {
        nodes {
          title
          language: node_locale
          megaMenuItems {
            id
            title
            menuType
            url
            navigationItems {
              ...NavbarMainMenuNavigationItemFragment
              childNavigationItems {
                ...NavbarMainMenuNavigationItemFragment
                childNavigationItems {
                  ...NavbarMainMenuNavigationItemFragment
                }
              }
            }
          }
        }
      }
    }
  `);
  const { language } = useUserContext();
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);

  // Choose the mega menu based on type and language
  let megaMenuTitle = 'Mega Menu';
  if (type !== 'Normal') megaMenuTitle = `${type} ${megaMenuTitle}`;
  const contentfulMegaMenu = data.allContentfulMegaMenu.nodes.find(
    (node) => node.language === language
      && node.title === megaMenuTitle
  );
  const { megaMenuItems } = contentfulMegaMenu || { megaMenuItems: [] };

  return (
    <Nav aria-label={options['navbar.main_menu_label']} className={className}>
      <TopMenu>
        {megaMenuItems.map((megaMenuItem) => {
          const { navigationItems } = megaMenuItem;
          let panelWidth = 'small';
          let hideLabel = false;
          let menuItems = null;

          if (megaMenuItem.menuType === 'Normal') {
            menuItems = <DropdownMenu items={navigationItems} />;
          } else if (megaMenuItem.menuType === 'Cards') {
            menuItems = <MenuCards items={navigationItems} />;
            panelWidth = 'medium';
          } else if (megaMenuItem.menuType === 'Columns') {
            menuItems = <MenuColumns items={navigationItems} />;
            panelWidth = 'large';
            hideLabel = true;
          }

          return (
            <MenuItem key={megaMenuItem.id}>
              <Dropdown>
                <DropdownFocusWrapper
                  hasDropdown={navigationItems && navigationItems.length > 0}
                  setIsDropdownOpen={setIsDropdownOpen}
                >
                  <MenuLink
                    to={megaMenuItem.url}
                    hasDropdown={navigationItems && navigationItems.length > 0}
                  >
                    <TextRenderer text={megaMenuItem.title} />
                  </MenuLink>
                  {navigationItems && navigationItems.length > 0 && (
                    <DropdownPanel
                      label={megaMenuItem.title}
                      width={panelWidth}
                      hideLabel={hideLabel}
                    >
                      {menuItems}
                    </DropdownPanel>
                  )}
                </DropdownFocusWrapper>
              </Dropdown>
            </MenuItem>
          );
        })}
      </TopMenu>
    </Nav>
  );
};

NavbarMainMenu.propTypes = {
  type: PropTypes.string,
  className: PropTypes.string,
};

export default NavbarMainMenu;
